import { FEEDBACK_SCREENSHOT_LIMIT } from './feedbackScreenshots.mjs';

export interface FeedbackRetryItem {
  uid: string;
  reportId: string;
  /** The FIRST screenshot's path, the shape every older queued item has. */
  screenshotPath: string | null;
  /**
   * Every screenshot path for the report, the first included (up to 5).
   * Absent on an item queued before multiple screenshots, which had only
   * screenshotPath; read through feedbackRetryScreenshotPaths.
   */
  screenshotPaths?: string[];
  needsEmail: boolean;
  needsScreenshot: boolean;
  retryAfter: number;
}

export interface FeedbackRetryStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
  key: (index: number) => string | null;
  readonly length: number;
}

export interface FeedbackRetryHandlers {
  retryEmail: (item: FeedbackRetryItem) => Promise<'sent' | 'not-configured' | 'failed' | 'unavailable'>;
  retryScreenshot: (item: FeedbackRetryItem) => Promise<'ready' | 'failed' | 'pending'>;
}

const STORAGE_PREFIX = 'ldg-feedback-retry-outbox-v2:';
const RETRY_BACKOFF_MS = 6 * 60 * 60 * 1000;
export const FEEDBACK_RETRY_READY_EVENT = 'ldg-feedback-retry-ready';

function storageKey(uid: string, reportId: string): string {
  return `${STORAGE_PREFIX}${encodeURIComponent(uid)}:${encodeURIComponent(reportId)}`;
}

function browserStorage(): FeedbackRetryStorage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function validItem(value: unknown): value is FeedbackRetryItem {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const item = value as Partial<FeedbackRetryItem>;
  return typeof item.uid === 'string'
    && item.uid.length > 0
    && typeof item.reportId === 'string'
    && item.reportId.length > 0
    && (item.screenshotPath === null || typeof item.screenshotPath === 'string')
    && (item.screenshotPaths === undefined || (
      Array.isArray(item.screenshotPaths)
      && item.screenshotPaths.length <= FEEDBACK_SCREENSHOT_LIMIT
      && item.screenshotPaths.every((path) => typeof path === 'string' && path.length > 0)
    ))
    && typeof item.needsEmail === 'boolean'
    && typeof item.needsScreenshot === 'boolean'
    && typeof item.retryAfter === 'number'
    && Number.isFinite(item.retryAfter)
    && item.retryAfter >= 0;
}

export function listFeedbackRetries(
  storage: FeedbackRetryStorage | null = browserStorage(),
): FeedbackRetryItem[] {
  if (!storage) return [];
  try {
    const items: FeedbackRetryItem[] = [];
    for (let index = 0; index < storage.length && items.length < 100; index += 1) {
      const key = storage.key(index);
      if (!key?.startsWith(STORAGE_PREFIX)) continue;
      const parsed = JSON.parse(storage.getItem(key) ?? 'null') as unknown;
      if (validItem(parsed)) items.push(parsed);
    }
    return items;
  } catch {
    return [];
  }
}

function readFeedbackRetry(
  uid: string,
  reportId: string,
  storage: FeedbackRetryStorage | null,
): FeedbackRetryItem | null {
  if (!storage) return null;
  try {
    const parsed = JSON.parse(storage.getItem(storageKey(uid, reportId)) ?? 'null') as unknown;
    return validItem(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function queueFeedbackRetry(
  item: FeedbackRetryItem,
  storage: FeedbackRetryStorage | null = browserStorage(),
): boolean {
  if (!storage) return false;
  try {
    const existing = readFeedbackRetry(item.uid, item.reportId, storage);
    // An empty list never erases a fuller one already queued for the report.
    const screenshotPaths = item.screenshotPaths?.length
      ? item.screenshotPaths
      : existing?.screenshotPaths;
    const next = existing ? {
      ...existing,
      screenshotPath: item.screenshotPath ?? existing.screenshotPath,
      ...(screenshotPaths ? { screenshotPaths } : {}),
      needsEmail: existing.needsEmail || item.needsEmail,
      needsScreenshot: existing.needsScreenshot || item.needsScreenshot,
      retryAfter: Math.max(existing.retryAfter, item.retryAfter),
    } : item;
    storage.setItem(storageKey(item.uid, item.reportId), JSON.stringify(next));
    return true;
  } catch {
    return false;
  }
}

export function resolveFeedbackRetry(
  uid: string,
  reportId: string,
  resolved: { email?: boolean; screenshot?: boolean },
  storage: FeedbackRetryStorage | null = browserStorage(),
): boolean {
  if (!storage) return false;
  try {
    const item = readFeedbackRetry(uid, reportId, storage);
    if (!item) return true;
    const updated = {
      ...item,
      needsEmail: resolved.email ? false : item.needsEmail,
      needsScreenshot: resolved.screenshot ? false : item.needsScreenshot,
    };
    const key = storageKey(uid, reportId);
    if (updated.needsEmail || updated.needsScreenshot) {
      storage.setItem(key, JSON.stringify(updated));
    } else {
      storage.removeItem(key);
    }
    return true;
  } catch {
    return false;
  }
}

export function releaseFeedbackRetry(
  uid: string,
  reportId: string,
  retryAfter: number,
  storage: FeedbackRetryStorage | null = browserStorage(),
): boolean {
  if (!storage) return false;
  try {
    const item = readFeedbackRetry(uid, reportId, storage);
    if (!item) return true;
    storage.setItem(storageKey(uid, reportId), JSON.stringify({ ...item, retryAfter }));
    return true;
  } catch {
    return false;
  }
}

/**
 * Every screenshot a queued report may hold. An item queued before multiple
 * screenshots has only its one screenshotPath, and that is all it ever had.
 */
export function feedbackRetryScreenshotPaths(item: FeedbackRetryItem): string[] {
  if (item.screenshotPaths && item.screenshotPaths.length > 0) return [...item.screenshotPaths];
  return item.screenshotPath ? [item.screenshotPath] : [];
}

/**
 * Retry only records owned by the currently signed-in member. Unresolved
 * work stays in local storage and is attempted on the next app start,
 * reconnect, or scheduled in-app pass.
 */
export async function drainFeedbackRetries(
  uid: string,
  handlers: FeedbackRetryHandlers,
  storage: FeedbackRetryStorage | null = browserStorage(),
  now = Date.now(),
): Promise<void> {
  const due = listFeedbackRetries(storage).filter((item) => item.uid === uid && item.retryAfter <= now);
  for (const item of due) {
    let unresolved = false;
    if (item.needsScreenshot && item.screenshotPath) {
      try {
        const screenshot = await handlers.retryScreenshot(item);
        if (screenshot !== 'pending') {
          resolveFeedbackRetry(uid, item.reportId, { screenshot: true }, storage);
        } else {
          unresolved = true;
        }
      } catch {
        unresolved = true;
      }
    }
    if (item.needsEmail) {
      try {
        const email = await handlers.retryEmail(item);
        if (email === 'sent') {
          resolveFeedbackRetry(uid, item.reportId, { email: true }, storage);
        } else {
          unresolved = true;
        }
      } catch {
        unresolved = true;
      }
    }
    if (unresolved) {
      releaseFeedbackRetry(uid, item.reportId, now + RETRY_BACKOFF_MS, storage);
    }
  }
}
